import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaTrophy, FaUsers, FaCalendar, FaDollarSign, FaArrowRight, FaMedal, FaCrown, FaChartLine, FaHome, FaGraduationCap, FaComments, FaQuestionCircle, FaFlask } from 'react-icons/fa';
import ChallengeService from '../services/ChallengeService';
import LeagueChallengesService from '../services/LeagueChallengesService';
import AuthService from '../services/AuthService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import BottomNav from './BottomNav';
import './Challenges.css';

const Challenges = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [leagueChallenges, setLeagueChallenges] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'upcoming', 'past'

  useEffect(() => {
    const checkAuth = async () => {
      if (!AuthService.isAuthenticated() || !state.user) {
        navigate('/login', { replace: true });
        return;
      }
      soundEffects.playPageTransition();
      await loadChallenges();
    };
    checkAuth();
  }, [navigate, state.user, filter]);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      
      // Load global challenges
      const globalData = await ChallengeService.getChallenges(filter);
      const globalChallenges = (globalData.challenges || []).map(ch => ({ ...ch, isLeagueChallenge: false }));
      
      // Load league challenges if user is a student
      let leagueChallengesData = [];
      if (state.user?.leagueId) {
        try {
          const leagueData = await LeagueChallengesService.getChallengesForStudent(state.user.leagueId);
          leagueChallengesData = (leagueData.challenges || []).map(ch => ({ ...ch, isLeagueChallenge: true }));
        } catch (error) {
          // Silently fail if student can't access league challenges
          console.log('Could not load league challenges:', error);
        }
      }
      
      // Combine and filter challenges
      const allChallenges = [...globalChallenges, ...leagueChallengesData];
      setChallenges(allChallenges);
      setLeagueChallenges(leagueChallengesData);
    } catch (error) {
      toast.error(error.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };



  const viewChallengeDetails = (challenge) => {
    // If it's a league challenge, navigate to a special route or pass flag
    if (challenge.isLeagueChallenge && state.user?.leagueId) {
      navigate(`/challenges/league/${state.user.leagueId}/${challenge.id}`);
    } else {
      navigate(`/challenges/${challenge.id}`);
    }
  };

  const getLeagueLabel = (league) => {
    const labels = { '10k': '$10,000', '100k': '$100,000', '1M': '$1,000,000' };
    return labels[league] || league;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChallengeStatus = (challenge) => {
    const now = new Date();
    const start = new Date(challenge.startDate);
    const end = new Date(challenge.endDate);

    if (now < start) return 'upcoming';
    if (now > end) return 'past';
    if (challenge.isActive) return 'active';
    return 'inactive';
  };

  if (loading) {
    return (
      <div className="challenges-page">
        <div className="loading">Loading challenges...</div>
      </div>
    );
  }

  return (
    <div className="challenges-page">
      <div className="challenges-header">
        <div className="header-content">
          <h1><FaTrophy /> Trading Challenges</h1>
          <p>Compete in monthly global challenges and climb the leaderboard!</p>
        </div>
      </div>

      <div className="challenges-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All Challenges
        </button>
        <button
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={filter === 'upcoming' ? 'active' : ''}
          onClick={() => setFilter('upcoming')}
        >
          Upcoming
        </button>
        <button
          className={filter === 'past' ? 'active' : ''}
          onClick={() => setFilter('past')}
        >
          Past
        </button>
      </div>

      <div className="challenges-grid">
        {challenges.length === 0 ? (
          <div className="empty-state">
            <FaTrophy size={64} />
            <h2>No challenges available</h2>
            <p>Check back soon for new challenges!</p>
          </div>
        ) : (
          challenges.map((challenge) => {
            const status = getChallengeStatus(challenge);
            const canJoin = status === 'active';

            return (
              <motion.div
                key={challenge.id}
                className="challenge-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="challenge-card-header">
                  <h3>{challenge.title}</h3>
                  <span className={`status-badge ${status}`}>
                    {status === 'active' && '🟢 Active'}
                    {status === 'upcoming' && '⏰ Upcoming'}
                    {status === 'past' && '🏁 Ended'}
                  </span>
                </div>

                {challenge.description && (
                  <p className="challenge-description">{challenge.description}</p>
                )}

                <div className="challenge-info">
                  {challenge.isLeagueChallenge && (
                    <div className="info-item league-badge">
                      <FaGraduationCap />
                      <span>League Challenge</span>
                    </div>
                  )}
                  <div className="info-item">
                    <FaDollarSign />
                    <span>{getLeagueLabel(challenge.league)} League</span>
                  </div>
                  <div className="info-item">
                    <FaUsers />
                    <span>{challenge.participant_count || challenge.participantCount || 0} participants</span>
                  </div>
                  <div className="info-item">
                    <FaCalendar />
                    <span>{formatDate(challenge.startDate)}</span>
                  </div>
                  {challenge.prizePool && (
                    <div className="info-item prize">
                      <FaTrophy />
                      <span>Prize: {formatCurrency(challenge.prizePool)}</span>
                    </div>
                  )}
                </div>

                <div className="challenge-actions">
                  <button
                    className="btn-view"
                    onClick={(e) => {
                      createRipple(e);
                      viewChallengeDetails(challenge);
                    }}
                  >
                    <FaChartLine /> View Details
                  </button>
                  {canJoin && (
                    <button
                      className="btn-join"
                      onClick={(e) => {
                        createRipple(e);
                        viewChallengeDetails(challenge);
                      }}
                    >
                      <FaArrowRight /> Join Challenge
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>


      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Challenges;


